import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Film, Sparkles, ImageIcon, ShoppingCart, Clock, Crown, Layers, Link2 } from "lucide-react";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const Dashboard = () => {
  const [reels, setReels] = useState([]);
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      // Fetch reels and carousels in parallel — neither blocks the other
      // and we want both lists visible together on the dashboard.
      const [reelsRes, carouselsRes] = await Promise.allSettled([
        api.get("/reels"),
        api.get("/carousels"),
      ]);
      if (reelsRes.status === "fulfilled") setReels(reelsRes.value.data);
      if (carouselsRes.status === "fulfilled") setCarousels(carouselsRes.value.data);
    } catch (e) {
      toast.error("Errore nel caricamento dei contenuti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminare questo reel?")) return;
    try {
      await api.delete(`/reels/${id}`);
      setReels((r) => r.filter((x) => x.id !== id));
      toast.success("Reel eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const onDeleteCarousel = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminare questo carosello?")) return;
    try {
      await api.delete(`/carousels/${id}`);
      setCarousels((c) => c.filter((x) => x.id !== id));
      toast.success("Carosello eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  // ---- Campaign grouping ----------------------------------------------
  // When the user has built a reel + carousel from the same idea (via the
  // CrossCreatePrompt flow), both items share a `campaign_id`. We group
  // them into a single card so the dashboard reflects the "campaign"
  // mental model instead of two disconnected items.
  const campaignsMap = new Map(); // campaign_id -> { reel, carousel }
  const standaloneReels = [];
  const standaloneCarousels = [];
  for (const r of reels) {
    if (r.campaign_id) {
      const entry = campaignsMap.get(r.campaign_id) || {};
      entry.reel = r;
      campaignsMap.set(r.campaign_id, entry);
    } else {
      standaloneReels.push(r);
    }
  }
  for (const c of carousels) {
    if (c.campaign_id) {
      const entry = campaignsMap.get(c.campaign_id) || {};
      entry.carousel = c;
      campaignsMap.set(c.campaign_id, entry);
    } else {
      standaloneCarousels.push(c);
    }
  }
  // Only render campaign cards when BOTH sides are present. If one side is
  // gone (deleted), show the survivor as standalone in its own section.
  const campaigns = [];
  for (const [cid, pair] of campaignsMap.entries()) {
    if (pair.reel && pair.carousel) {
      campaigns.push({ id: cid, reel: pair.reel, carousel: pair.carousel });
    } else if (pair.reel) {
      standaloneReels.push(pair.reel);
    } else if (pair.carousel) {
      standaloneCarousels.push(pair.carousel);
    }
  }
  // Sort campaigns by the most-recent member, descending.
  campaigns.sort((a, b) => {
    const at = Math.max(
      new Date(a.reel.created_at).getTime(),
      new Date(a.carousel.created_at).getTime(),
    );
    const bt = Math.max(
      new Date(b.reel.created_at).getTime(),
      new Date(b.carousel.created_at).getTime(),
    );
    return bt - at;
  });

  // Single-click "elimina campagna" — wipes both the reel and the
  // carousel sharing this campaign_id, including their media on the
  // backend. Confirms before destruction.
  const deleteCampaign = async (campaign) => {
    const { reel, carousel } = campaign || {};
    const ok = window.confirm(
      `Eliminare l'intera campagna?\n\n` +
      `Verranno cancellati DEFINITIVAMENTE:\n` +
      `  • Reel: ${(reel?.title || "—").slice(0, 60)}\n` +
      `  • Carosello: ${(carousel?.topic || carousel?.title || "—").slice(0, 60)}\n\n` +
      `Questa operazione non può essere annullata.`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete(`/campaigns/${campaign.id}`);
      toast.success(`Campagna eliminata · ${data.reels_deleted} reel + ${data.carousels_deleted} caroselli`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione campagna");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
              · La tua libreria
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white">
              I tuoi Reel
            </h1>
            <p className="text-zinc-400 font-body mt-2">
              {reels.length === 0 && carousels.length === 0
                ? "Nessun contenuto ancora. Crea il primo!"
                : `${reels.length} reel · ${carousels.length} caroselli${campaigns.length ? ` · ${campaigns.length} campagne` : ""}`}
            </p>
          </div>
          <button
            data-testid="dashboard-create-reel"
            onClick={() => navigate("/create")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuovo contenuto
          </button>
        </div>

        <FreeTrialBanner user={user} />
        <ImageBudgetWidget user={user} />

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 py-20">
            <span className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot" />
            <span className="font-mono text-xs tracking-widest uppercase">Loading reels...</span>
          </div>
        ) : (
          <>
            {/* Campaigns: reel + carousel pairs created from the same idea */}
            {campaigns.length > 0 && (
              <div className="mb-14">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-violet-400 mb-3">
                      · Campagne · reel + carosello accoppiati
                    </div>
                    <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">
                      Le tue campagne
                    </h2>
                    <p className="text-zinc-400 font-body mt-2 text-sm">
                      {campaigns.length} {campaigns.length === 1 ? "campagna" : "campagne"} · ogni campagna ha un reel video e un carosello social pensati per la stessa idea
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="dashboard-campaigns-grid">
                  {campaigns.map((camp) => (
                    <CampaignCard key={camp.id} campaign={camp} navigate={navigate} onDelete={deleteCampaign} />
                  ))}
                </div>
              </div>
            )}

            {/* Standalone reels (no campaign link, or campaign with deleted carousel) */}
            {standaloneReels.length === 0 && standaloneCarousels.length === 0 && campaigns.length === 0 ? (
              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-12 md:p-20 text-center" data-testid="dashboard-empty-state">
                <div className="w-16 h-16 mx-auto mb-6 rounded-sm bg-[#FF4400]/10 border border-[#FF4400]/40 flex items-center justify-center">
                  <Film className="w-7 h-7 text-[#FF4400]" />
                </div>
                <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter text-white mb-3">
                  Nessun reel. Ancora.
                </h2>
                <p className="text-zinc-400 font-body max-w-md mx-auto mb-8">
                  Il tuo primo reel promozionale generato dall'AI è a 60 secondi di distanza.
                </p>
                <Link
                  to="/create"
                  data-testid="dashboard-empty-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  Crea il tuo primo Reel
                </Link>
              </div>
            ) : standaloneReels.length > 0 ? (
              <div>
                {campaigns.length > 0 && (
                  <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-4 mt-2">
                    · Reel singoli
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {standaloneReels.map((reel) => (
              <Link
                key={reel.id}
                to={`/reel/${reel.id}`}
                data-testid={`reel-card-${reel.id}`}
                className="group relative border border-zinc-800 bg-zinc-950 rounded-sm overflow-hidden hover:border-[#FF4400] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="aspect-[9/16] bg-zinc-900 relative overflow-hidden">
                  {reel.thumbnail ? (
                    <img
                      src={`data:image/png;base64,${reel.thumbnail}`}
                      alt={reel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-10 h-10 text-zinc-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20" />
                  <div className="absolute top-2 left-2">
                    <span className="inline-block px-2 py-1 bg-black/60 backdrop-blur text-[9px] font-mono uppercase tracking-widest text-white border border-white/10 rounded-sm">
                      {reel.category}
                    </span>
                  </div>
                  <button
                    onClick={(e) => onDelete(reel.id, e)}
                    data-testid={`reel-delete-${reel.id}`}
                    className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#FF4400] hover:border-[#FF4400] opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-display font-black text-white text-sm leading-tight tracking-tight mb-1 line-clamp-2">
                      {reel.title}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                      {formatDate(reel.created_at)}
                    </div>
                  </div>
                </div>
              </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* Carousels section — only standalone (campaign-paired carousels
            already appear inside the campaign card above). */}
        {!loading && standaloneCarousels.length > 0 && (
          <div className="mt-16">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
                  · Caroselli social
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">
                  Caroselli singoli
                </h2>
                <p className="text-zinc-400 font-body mt-2 text-sm">
                  {standaloneCarousels.length} {standaloneCarousels.length === 1 ? "carosello" : "caroselli"} non ancora accoppiati a un reel
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-carousels-grid">
              {standaloneCarousels.map((c) => (
                <Link
                  key={c.id}
                  to={`/carousel/${c.id}`}
                  data-testid={`carousel-card-${c.id}`}
                  className="group relative border border-zinc-800 bg-zinc-950 rounded-sm overflow-hidden hover:border-[#FF4400] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[4/5] bg-zinc-900 relative overflow-hidden">
                    {c.cover_image ? (
                      <img
                        src={`data:image/jpeg;base64,${c.cover_image}`}
                        alt={c.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, #FF4400 0%, #7a2200 100%)" }}
                      >
                        <span className="font-display font-black text-2xl text-white/30">
                          {(c.title || "").charAt(0).toUpperCase() || "C"}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20" />
                    <div className="absolute top-2 left-2">
                      <span className="inline-block px-2 py-1 bg-black/60 backdrop-blur text-[9px] font-mono uppercase tracking-widest text-white border border-white/10 rounded-sm">
                        {c.num_slides} slide
                      </span>
                    </div>
                    <button
                      onClick={(e) => onDeleteCarousel(c.id, e)}
                      data-testid={`carousel-delete-${c.id}`}
                      className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-400 hover:text-[#FF4400] hover:border-[#FF4400] opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="font-display font-black text-white text-sm leading-tight tracking-tight mb-1 line-clamp-2">
                        {c.title}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
                        {formatDate(c.created_at)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PLAN_IMAGE_QUOTA = { free: 12, pro: 50, agency: 999 };

const CampaignCard = ({ campaign, navigate, onDelete }) => {
  const { reel, carousel } = campaign;
  // Use the carousel topic when present (it's editorial and reliable), else
  // fall back to the reel title.
  const headline = (carousel.topic || reel.title || "Campagna").trim();
  const businessName = carousel.business_name || reel.business_name || "";
  const latest = Math.max(
    new Date(reel.created_at).getTime(),
    new Date(carousel.created_at).getTime(),
  );
  return (
    <div
      data-testid={`campaign-card-${campaign.id}`}
      className="group relative border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-zinc-950 to-[#FF4400]/5 rounded-md overflow-hidden hover:border-violet-400 transition-all duration-300"
    >
      {/* Linked badge */}
      <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 bg-violet-600/90 backdrop-blur text-[10px] font-mono uppercase tracking-widest text-white rounded-sm shadow-sm">
        <Link2 className="w-3 h-3" />
        Campagna
      </div>

      <div className="grid grid-cols-2 gap-0">
        {/* Reel preview (9:16) */}
        <button
          type="button"
          onClick={() => navigate(`/reel/${reel.id}`)}
          data-testid={`campaign-reel-link-${campaign.id}`}
          className="relative aspect-[9/16] bg-zinc-900 overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-violet-400"
        >
          {reel.thumbnail ? (
            <img
              src={`data:image/png;base64,${reel.thumbnail}`}
              alt={reel.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film className="w-10 h-10 text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
            <Film className="w-3 h-3 text-white" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-white">Reel</span>
          </div>
        </button>

        {/* Carousel preview (4:5) — wrapped in a 9:16 container so heights align */}
        <button
          type="button"
          onClick={() => navigate(`/carousel/${carousel.id}`)}
          data-testid={`campaign-carousel-link-${campaign.id}`}
          className="relative aspect-[9/16] bg-zinc-900 overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-violet-400"
        >
          {carousel.cover_image ? (
            <img
              src={`data:image/jpeg;base64,${carousel.cover_image}`}
              alt={carousel.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #FF4400 0%, #7a2200 100%)" }}
            >
              <Layers className="w-10 h-10 text-white/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
            <Layers className="w-3 h-3 text-white" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-white">
              Carosello · {carousel.num_slides}
            </span>
          </div>
        </button>
      </div>

      {/* Campaign meta */}
      <div className="p-4 md:p-5">
        <div className="font-display font-black text-white text-lg md:text-xl leading-tight tracking-tight mb-1 line-clamp-2">
          {headline}
        </div>
        {businessName && (
          <div className="text-sm text-zinc-400 font-body mb-3 truncate">
            {businessName}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 mt-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {formatDate(new Date(latest).toISOString())}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/reel/${reel.id}`)}
              data-testid={`campaign-open-reel-${campaign.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white bg-zinc-800 hover:bg-zinc-700 rounded-sm transition-colors"
            >
              <Film className="w-3 h-3" />
              Reel
            </button>
            <button
              onClick={() => navigate(`/carousel/${carousel.id}`)}
              data-testid={`campaign-open-carousel-${campaign.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white bg-violet-600 hover:bg-violet-500 rounded-sm transition-colors"
            >
              <Layers className="w-3 h-3" />
              Carosello
            </button>
            {/* Single-click "delete the whole campaign" — wipes both the
                reel and the carousel + their media. Requires confirmation
                because it's destructive and not reversible. */}
            <button
              onClick={() => onDelete && onDelete(campaign)}
              data-testid={`campaign-delete-${campaign.id}`}
              title="Elimina campagna (reel + carosello)"
              className="inline-flex items-center justify-center w-9 h-9 text-xs font-mono uppercase tracking-widest text-zinc-400 border border-zinc-800 hover:text-red-400 hover:border-red-500/60 rounded-sm transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FreeTrialBanner = ({ user }) => {
  if (!user || user.plan !== "free") return null;
  const expired = user.free_trial_active === false;
  const daysLeft = Math.max(0, user.free_trial_days_left || 0);
  if (expired) {
    return (
      <div
        className="mb-6 border border-[#FF4400] bg-gradient-to-r from-[#FF4400]/15 via-[#FF4400]/10 to-transparent rounded-sm p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
        data-testid="free-trial-banner-expired"
      >
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-sm bg-[#FF4400] text-white flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-1">· Trial gratuito scaduto</div>
            <div className="font-display font-black text-white text-lg md:text-xl tracking-tighter">
              I 30 giorni gratuiti sono finiti
            </div>
            <div className="text-zinc-300 font-body text-sm mt-1">
              Passa a Pro a soli <span className="text-[#FF4400] font-bold">€10/mese</span> per continuare a generare reel e immagini AI.
            </div>
          </div>
        </div>
        <Link
          to="/pricing"
          data-testid="free-trial-upgrade-cta"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white rounded-sm font-display font-bold text-sm transition-colors flex-shrink-0"
        >
          <Sparkles className="w-4 h-4" />
          Passa a Pro €10/mese
        </Link>
      </div>
    );
  }
  if (daysLeft <= 7) {
    return (
      <div
        className="mb-6 border border-zinc-800 bg-zinc-950 rounded-sm p-4 flex items-center gap-3"
        data-testid="free-trial-banner-warning"
      >
        <Clock className="w-4 h-4 text-[#FF4400] flex-shrink-0" />
        <div className="flex-1 text-sm text-zinc-300 font-body">
          Trial gratuito · <span className="text-[#FF4400] font-bold">{daysLeft} {daysLeft === 1 ? "giorno" : "giorni"}</span> rimanenti.
          Dopo, <Link to="/pricing" className="text-white underline hover:text-[#FF4400]">passa a Pro €10/mese</Link> per continuare.
        </div>
      </div>
    );
  }
  return null;
};

const ImageBudgetWidget = ({ user }) => {
  if (!user) return null;
  const quota = PLAN_IMAGE_QUOTA[user.plan] ?? 0;
  const used = user.images_used_this_month || 0;
  const extra = user.extra_images_balance || 0;
  const remaining = Math.max(0, quota - used);
  const total = remaining + extra;
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const lowBudget = quota > 0 && total < quota * 0.2;
  const isFree = user.plan === "free";

  return (
    <div className="mb-8 border border-zinc-800 bg-zinc-950 rounded-sm p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="image-budget-widget">
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${lowBudget ? "bg-[#FF4400]/15 text-[#FF4400]" : "bg-zinc-900 text-[#FF4400]"}`}>
          <ImageIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Immagini AI {isFree ? "Free" : "questo mese"}</div>
          <div className="font-display font-black text-2xl text-white tracking-tighter" data-testid="img-budget-total">
            {total} <span className="text-zinc-500 font-mono text-sm font-normal">disponibili</span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1.5">
          <span>{used}/{quota} usate</span>
          {extra > 0 && <span className="text-emerald-400">+{extra} extra</span>}
        </div>
        <div className="w-full h-2 bg-zinc-900 rounded-sm overflow-hidden">
          <div
            className={`h-full ${lowBudget ? "bg-[#FF4400]" : "bg-emerald-500"} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {lowBudget && (
          <p className="text-xs text-[#FF4400] font-mono uppercase tracking-widest mt-2">
            · Budget basso · pensaci ora
          </p>
        )}
      </div>

      {!isFree && (
        <Link
          to="/pricing#image-packs"
          data-testid="img-budget-buy-cta"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#FF4400] text-[#FF4400] hover:bg-[#FF4400] hover:text-white rounded-sm font-display font-bold text-sm transition-colors flex-shrink-0"
        >
          <ShoppingCart className="w-4 h-4" />
          Compra extra
        </Link>
      )}
      {isFree && (
        <Link
          to="/pricing"
          data-testid="img-budget-upgrade-cta"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FF4400] hover:bg-[#FF6633] text-white rounded-sm font-display font-bold text-sm transition-colors flex-shrink-0"
        >
          <Sparkles className="w-4 h-4" />
          Passa a Pro
        </Link>
      )}
    </div>
  );
};

export default Dashboard;
